import React, { useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';

const BarcodeLabel = ({ value, name, price, width = 2, height = 50, fontSize = 14 }) => {
    const barcodeRef = useRef(null);
    const [labelQty, setLabelQty] = useState(1);

    // Read barcode size from localStorage settings
    const getBarcodeSize = () => {
        const size = localStorage.getItem('barcode_size') || 'medium';
        const sizes = {
            small: { width: 1.5, height: 35, fontSize: 10, labelW: '30mm', labelH: '20mm' },
            medium: { width: 2, height: 50, fontSize: 12, labelW: '50mm', labelH: '25mm' },
            large: { width: 2.5, height: 65, fontSize: 14, labelW: '70mm', labelH: '40mm' },
        };
        if (size === 'custom') {
            return {
                width: parseFloat(localStorage.getItem('barcode_custom_width')) || 2,
                height: parseFloat(localStorage.getItem('barcode_custom_height')) || 50,
                fontSize: 12,
                labelW: (localStorage.getItem('barcode_label_w') || '50') + 'mm',
                labelH: (localStorage.getItem('barcode_label_h') || '25') + 'mm',
            };
        }
        return sizes[size] || sizes.medium;
    };

    useEffect(() => {
        if (barcodeRef.current && value) {
            try {
                const sizeConfig = getBarcodeSize();
                // Dynamically adjust width factor if SKU is long
                let barWidth = width || sizeConfig.width;
                if (value.length > 12) barWidth = Math.max(1, barWidth * 0.7);
                if (value.length > 20) barWidth = Math.max(1, barWidth * 0.5);

                JsBarcode(barcodeRef.current, value, {
                    format: "CODE128",
                    width: barWidth,
                    height: height || sizeConfig.height,
                    displayValue: true,
                    fontSize: fontSize || sizeConfig.fontSize,
                    margin: 10,
                    background: "#ffffff",
                    lineColor: "#000000"
                });
            } catch (err) {
                console.error("Barcode generation failed:", err);
            }
        }
    }, [value, width, height, fontSize]);

    const handlePrint = () => {
        const sizeConfig = getBarcodeSize();
        const labelsHtml = Array.from({ length: labelQty }, (_, i) => `
            <div class="label-container" style="width: ${sizeConfig.labelW}; page-break-inside: avoid; margin-bottom: 5mm;">
                <div class="product-name">${name || 'Product'}</div>
                <svg id="barcode-${i}"></svg>
                <div class="product-price">Rs. ${parseFloat(price || 0).toLocaleString()}</div>
            </div>
        `).join('');

        let barWidth = sizeConfig.width;
        if (value.length > 12) barWidth = Math.max(1, barWidth * 0.7);
        if (value.length > 20) barWidth = Math.max(1, barWidth * 0.5);

        const barcodeInitScripts = Array.from({ length: labelQty }, (_, i) => `
            JsBarcode("#barcode-${i}", "${value}", {
                format: "CODE128",
                width: ${barWidth},
                height: ${sizeConfig.height},
                displayValue: true,
                fontSize: ${sizeConfig.fontSize},
                margin: 5,
                background: "#ffffff",
                lineColor: "#000000"
            });
        `).join('\n');

        const printWindow = window.open('', '_blank', 'width=600,height=800');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Barcode Labels - ${value}</title>
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
                    <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { 
                            display: flex; 
                            flex-direction: column; 
                            align-items: center; 
                            padding: 10mm;
                            font-family: 'Segoe UI', sans-serif; 
                        }
                        .labels-grid {
                            display: flex;
                            flex-wrap: wrap;
                            gap: 5mm;
                            justify-content: center;
                        }
                        .label-container { 
                            text-align: center; 
                            border: 1px dashed #ccc; 
                            padding: 3mm;
                            border-radius: 3mm;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                        }
                        .label-container svg {
                            max-width: 100%;
                            height: auto;
                        }
                        .product-name { 
                            font-size: 10pt; 
                            font-weight: bold; 
                            margin-bottom: 2mm;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            width: 100%;
                        }
                        .product-price { font-size: 12pt; font-weight: 800; margin-top: 2mm; color: #1e1e2d; }
                        @media print {
                            body { padding: 0; }
                            .label-container { border: none; }
                        }
                    </style>
                </head>
                <body>
                    <div class="labels-grid">
                        ${labelsHtml}
                    </div>
                    <script>
                        window.onload = function() {
                            try {
                                ${barcodeInitScripts}
                            } catch(e) {
                                console.error('Barcode render error:', e);
                            }
                            setTimeout(function() {
                                window.print();
                                window.onafterprint = function() { window.close(); };
                            }, 600);
                        };
                    <\/script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <div className="barcode-wrapper d-flex flex-column align-items-center bg-white p-3 rounded border">
            <div className="fw-bold small mb-2 text-truncate w-100 text-center" style={{ color: '#000' }}>{name}</div>
            <svg ref={barcodeRef}></svg>
            <div className="fw-bold mt-1" style={{ color: '#000', fontSize: '1.1rem' }}>Rs. {parseFloat(price || 0).toLocaleString()}</div>
            <div className="mt-3 w-100">
                <div className="d-flex gap-2 align-items-center mb-2">
                    <label className="form-label small fw-bold mb-0 text-nowrap" style={{ color: '#333' }}>Labels:</label>
                    <input 
                        type="number" 
                        className="form-control form-control-sm rounded-pill text-center" 
                        value={labelQty} 
                        onChange={e => setLabelQty(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                        min="1" 
                        max="100"
                        style={{ width: '70px' }}
                    />
                    <div className="d-flex gap-1">
                        {[1, 4, 10, 20].map(n => (
                            <button key={n} className={`btn btn-sm ${labelQty === n ? 'btn-primary' : 'btn-outline-secondary'} rounded-pill px-2`} onClick={() => setLabelQty(n)} style={{ fontSize: '0.7rem', minWidth: '30px' }}>
                                {n}
                            </button>
                        ))}
                    </div>
                </div>
                <button className="btn btn-sm btn-primary w-100 rounded-pill fw-bold" onClick={handlePrint}>
                    <i className="bi bi-printer me-1"></i> Print {labelQty > 1 ? `${labelQty} Labels` : 'Label'}
                </button>
            </div>
        </div>
    );
};

export default BarcodeLabel;
